import { useMemo } from 'react';

import {
  type DataProviderChildren,
  DataProviderQueryStatus,
} from '../../next/CustomDash/components/DataProvider/types';
import { useUserPreferences } from './hooks/useUserPreferences';
import type { PreferenceScope, UserPreferences } from './types';

export interface UserPreferencesData {
  scope: PreferenceScope;
  savedPreferences: UserPreferences;
  preferences: UserPreferences;
  /** Re-sync saved preferences from the server using current local storage state */
  refetchSavedPreferences: () => Promise<void>;
}

export const UserPreferencesDataProvider = ({
  affectedScope,
  preferencesFromFilters,
  children,
}: {
  affectedScope: PreferenceScope;
  preferencesFromFilters?: Partial<UserPreferences>; // allows preferences controlled by filters to modify preference editor values
  children: DataProviderChildren<UserPreferencesData>;
}) => {
  const {
    preferences,
    savedPreferences,
    isInitialLoading,
    error,
    syncWithServer,
  } = useUserPreferences(affectedScope);

  const data = useMemo<UserPreferencesData>(
    () => ({
      scope: affectedScope,
      savedPreferences,
      // Prefer the actual filter-derived values for display, so editors reflect
      // what the page is currently using.
      preferences: {
        ...preferences,
        ...preferencesFromFilters,
        taxonomies: {
          // base stored prefs
          ...(preferences?.taxonomies || {}),
          // filter-derived override stored
          ...(preferencesFromFilters?.taxonomies || {}),
        },
      },
      refetchSavedPreferences: syncWithServer,
    }),
    [
      affectedScope,
      preferences,
      savedPreferences,
      preferencesFromFilters,
      syncWithServer,
    ]
  );

  let status = DataProviderQueryStatus.Success;
  if (isInitialLoading) status = DataProviderQueryStatus.Fetching;
  if (error) status = DataProviderQueryStatus.Error;

  return children({ data, status });
};
